import React, { useState, useCallback, useEffect } from 'react'
import { useTranslation, Trans } from 'react-i18next'
import { colors } from 'globalStyles'
import PopUp from 'reusableComponents/PopUp'
import Button from 'react-bootstrap/Button'
import PropTypes from 'prop-types'
import DropOffLocation from 'components/DropOffLocation/DropOffLocation'
import BuzzerCode from 'components/BuzzerCode'
import DeliveryInstructionss from 'components/DeliveryInstructionss'
import { ReactComponent as BuzzerCodeIcon } from 'assets/svg/buzer-code-pop-up.svg'
import { ReactComponent as DeliveryInstructionIcon } from 'assets/svg/delivery_address_icon.svg'
import { ReactComponent as DropOffLocationIcon } from 'assets/svg/house-building-black.svg'
import { Form } from 'react-bootstrap'
import { selectDeliverySummary } from 'store/shipping/shippingSelectors'
import { useSelector } from 'react-redux'
import SVGIcon from 'reusableComponents/SVGIcon'
import { getDOMEncodeString } from 'utils/helperFunctions'
import SuccessModal from 'components/SuccessModal'
import styled from 'styled-components'

const InfoStyledSpan = styled.span`
  font-size: 12px;
  font-weight: 400;
  line-height: 100%;
  letter-spacing: 0em;
  float: left;
  text-align: left;
  align-items: left;
  margin-top: 16px !important;
`

const StyledForm = styled(Form)`
  margin: 0;
  padding: 0;
`

const SetPreferencesPopUp = ({ show, onHide, popUpType, onSave }) => {
  const [validated, setValidated] = useState(false)

  const { t } = useTranslation()
  const {
    deliveryLocation,
    buzzerCode,
    additionalDeliveryInstructions,
    remoteSignaturePreference,
  } = useSelector(selectDeliverySummary)
  const [loc, setLoc] = useState(deliveryLocation)
  const [buzzCode, setBuzzCode] = useState(buzzerCode)
  const [instr, setInstr] = useState(additionalDeliveryInstructions)
  const [inputCount, setInputCount] = useState(0)
  const [showSuccessModal, setShowSuccessModal] = useState(false)
  const [successMessage, setSuccessMessage] = useState('')

  const preferencesValueChangeHandler = (event) => {
    setLoc(event.target.value)
  }

  const buzzCodeValueChangeHandler = (event) => {
    const buzCode = event.target.value
    const re = /^[0-9a-zA-Z\b]+$/
    if (buzCode !== '' && !re.test(buzCode)) return
    setBuzzCode(buzCode)
  }

  const instrCodeValueChangeHandler = (event) => {
    setInstr(event.target.value)
    setInputCount(event.target.value.length)
  }

  const cancelSuccessModal = () => {
    setShowSuccessModal(false)
  }

  // Sync local state with Redux store when values change
  useEffect(() => {
    setLoc(deliveryLocation)
  }, [deliveryLocation])

  useEffect(() => {
    setBuzzCode(buzzerCode)
  }, [buzzerCode])

  useEffect(() => {
    setInstr(additionalDeliveryInstructions)
    setInputCount(additionalDeliveryInstructions?.length || 0)
  }, [additionalDeliveryInstructions])

  useEffect(() => {
    if (showSuccessModal) {
      const timer = setTimeout(() => {
        setShowSuccessModal(false)
      }, 4000)
      return () => clearTimeout(timer)
    }
    return undefined
  }, [showSuccessModal])

  const handleSubmit = useCallback(
    (event) => {
      event.preventDefault()
      const form = event.currentTarget

      if (form.checkValidity() === false) {
        event.stopPropagation()
      }
      setValidated(true)

      // Determine the success message based on popUpType and current values
      let message = ''
      if (popUpType === 'deliveryInstructions') {
        message =
          additionalDeliveryInstructions && additionalDeliveryInstructions !== ''
            ? t('deliveryPreferencesSummary.instruction_update_success')
            : t('deliveryPreferencesSummary.instruction_add_success')
      } else if (popUpType === 'dropOffLocation') {
        message = t('deliveryPreferencesSummary.edited_preferences')
      } else if (popUpType === 'buzzerCode') {
        message = t('deliveryPreferencesSummary.edited_preferences')
      }
      setSuccessMessage(message)
      setShowSuccessModal(true)
      onSave(instr, buzzCode, loc, remoteSignaturePreference)
      onHide()
    },
    [
      instr,
      buzzCode,
      loc,
      remoteSignaturePreference,
      onSave,
      onHide,
      popUpType,
      t,
      additionalDeliveryInstructions,
    ]
  )

  // TODO: Update this object when implementing other functions
  // in "Personalize Your Delivery Experience"
  const modalType = {
    dropOffLocation: {
      title: t('set_preferences.where_to_leave'),
      icon: DropOffLocationIcon,
      saveText: t('buttonActions.save'),
      cancelText: t('buttonActions.cancel'),
      component: (
        <DropOffLocation
          id="dropOffLocation"
          onValueChangeHandler={preferencesValueChangeHandler}
          selectedItem={loc}
        />
      ),
      isSaveDisabled: loc === deliveryLocation,
      onCancel: () => {
        setLoc(deliveryLocation)
        onHide()
      },
    },
    buzzerCode: {
      title: t('delivery_preferences.buzzer_code'),
      icon: BuzzerCodeIcon,
      saveText: t('buttonActions.save'),
      cancelText: t('buttonActions.cancel'),
      component: (
        <BuzzerCode
          id="buzzerCode"
          buzzerCode={buzzCode}
          onValueChangeHandler={buzzCodeValueChangeHandler}
        />
      ),
      isSaveDisabled: buzzCode === buzzerCode,
      onCancel: () => {
        setBuzzCode(buzzerCode)
        onHide()
      },
    },
    deliveryInstructions: {
      title: t('set_preferences.add_del_instructions'),
      icon: DeliveryInstructionIcon,
      saveText: t('buttonActions.save'),
      cancelText: t('buttonActions.cancel'),
      component: (
        <DeliveryInstructionss
          id="additionalInstr"
          inputCount={inputCount}
          onValueChangeHandler={instrCodeValueChangeHandler}
          instructions={instr}
        />
      ),
      isSaveDisabled: instr === additionalDeliveryInstructions,
      footer: (
        <InfoStyledSpan>
          <Trans t={t} i18nKey="deliveryPreferencesSummary.instructions" />
        </InfoStyledSpan>
      ),
      onCancel: () => {
        setInstr(additionalDeliveryInstructions)
        onHide()
        if (additionalDeliveryInstructions) {
          setInputCount(additionalDeliveryInstructions.length)
        } else {
          setInputCount(0)
        }
      },
    },
  }
  // Set relative modelData based on the Type.
  const modalData = modalType[popUpType]

  // Safety check: return null if modalData is undefined
  if (!modalData) {
    return null
  }

  // Don't render the form if modal is not shown
  if (!show) {
    return null
  }

  return (
    <StyledForm id="preferenceOptionsForm" noValidate validated={validated} onSubmit={handleSubmit}>
      <PopUp
        show={show}
        primaryButton={
          <Button variant="secondary" onClick={modalData.onCancel}>
            {modalData.cancelText}
          </Button>
        }
        secondaryButton={
          <Button
            type="submit"
            variant="primary"
            form="preferenceOptionsForm"
            disabled={modalData.isSaveDisabled}
          >
            {modalData.saveText}
          </Button>
        }
        icon={getDOMEncodeString(<SVGIcon Icon={modalData.icon} color={colors.web_text_black} />)}
        iconWidth="25px"
        title={modalData.title}
        onHide={modalData.onCancel}
        footerContent={modalData.footer ? modalData.footer : null}
      >
        {modalData.component}
      </PopUp>
      {showSuccessModal && (
        <SuccessModal
          show={showSuccessModal}
          onHide={cancelSuccessModal}
          message={successMessage}
        />
      )}
    </StyledForm>
  )
}

SetPreferencesPopUp.propTypes = {
  show: PropTypes.bool.isRequired,
  onHide: PropTypes.func.isRequired,
  popUpType: PropTypes.string.isRequired,
  onSave: PropTypes.func.isRequired,
}

export default SetPreferencesPopUp
